/**
 * Map Layer Icon Components
 *
 * Renders map layer selector icons as React SVG components instead of using
 * dangerouslySetInnerHTML. This eliminates XSS risk from SVG injection.
 */

import { memo } from 'react';

export type LayerIconType = 'street' | 'satellite' | 'satellite-labels' | 'terrain';

interface MapLayerIconProps {
  type: LayerIconType;
  size?: number;
  className?: string;
}

/**
 * Street/Map view icon
 */
const StreetIcon = memo(({ size = 20 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-label="Street map">
    <path d="M20.5 3l-.16.03L15 5.1 9 3 3.36 4.9c-.21.07-.36.25-.36.48V20.5c0 .28.22.5.5.5l.16-.03L9 18.9l6 2.1 5.64-1.9c.21-.07.36-.25.36-.48V3.5c0-.28-.22-.5-.5-.5zM15 19l-6-2.11V5l6 2.11V19z" />
  </svg>
));
StreetIcon.displayName = 'StreetIcon';

/**
 * Satellite view icon
 */
const SatelliteIcon = memo(({ size = 20 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-label="Satellite view">
    <path d="M21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H3V5h18v14zm-5-7l-3 3.72L11 13l-4 5h14l-5-6z" />
  </svg>
));
SatelliteIcon.displayName = 'SatelliteIcon';

/**
 * Satellite + labels (hybrid) view icon
 */
const HybridIcon = memo(({ size = 20 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-label="Hybrid view">
    <path d="M21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H3V5h18v14zm-5-7l-3 3.72L11 13l-4 5h14l-5-6z" />
    <text x="3" y="10" fontSize="6" fontWeight="bold" fill="currentColor">Aa</text>
  </svg>
));
HybridIcon.displayName = 'HybridIcon';

/**
 * Terrain view icon
 */
const TerrainIcon = memo(({ size = 20 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-label="Terrain view">
    <path d="M14 6l-3.75 5 2.85 3.8-1.6 1.2C9.81 13.75 7 10 7 10l-6 8h22L14 6z" />
  </svg>
));
TerrainIcon.displayName = 'TerrainIcon';

/**
 * Map icon mapping
 */
const ICON_MAP: Record<LayerIconType, React.FC<{ size?: number }>> = {
  street: StreetIcon,
  satellite: SatelliteIcon,
  'satellite-labels': HybridIcon,
  terrain: TerrainIcon,
};

/**
 * Main Map Layer Icon Component
 */
const MapLayerIcon = memo(({ type, size = 20, className }: MapLayerIconProps) => {
  const IconComponent = ICON_MAP[type];

  if (!IconComponent) {
    return null;
  }

  return (
    <span className={className} style={{ display: 'inline-flex', alignItems: 'center' }}>
      <IconComponent size={size} />
    </span>
  );
});
MapLayerIcon.displayName = 'MapLayerIcon';

export default MapLayerIcon;

// Export individual icons for flexibility
export { StreetIcon, SatelliteIcon, HybridIcon, TerrainIcon };
